import { FFT, hann } from './fft';

/**
 * Onset / tempo analysis.
 *
 * Pipeline: mono downmix -> STFT -> half-wave-rectified spectral flux ->
 * adaptive-mean subtraction -> autocorrelation with harmonic reinforcement ->
 * beat phase via pulse-train correlation.
 */

export interface OnsetEnvelope {
  /** One value per analysis frame, already normalised and mean-subtracted. */
  values: Float32Array;
  /**
   * The same envelope built from low-frequency bins only. Snares generate more
   * spectral flux than kicks, so picking the "accented" beat from the full-band
   * envelope reliably finds the backbeat instead of the downbeat. The low band
   * is where the kick lives, and the kick is where bar 1 is.
   */
  low: Float32Array;
  /** Frames per second of the envelope. */
  rate: number;
  /**
   * Seconds to add to `frame / rate` to get real time. Flux for frame i is
   * computed over samples [i·HOP, i·HOP + FFT), so the frame is attributed to
   * its window centre; without this every onset reads several milliseconds
   * early and the whole chop grid drifts forward.
   */
  origin: number;
}

const FFT_SIZE = 1024;
const HOP = 256;

export function toMono(buffer: AudioBuffer): Float32Array {
  const n = buffer.length;
  const out = new Float32Array(n);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < n; i++) out[i] += data[i];
  }
  const inv = 1 / Math.max(1, buffer.numberOfChannels);
  for (let i = 0; i < n; i++) out[i] *= inv;
  return out;
}

export function onsetEnvelope(
  mono: Float32Array,
  sampleRate: number,
  onProgress?: (fraction: number) => void,
): OnsetEnvelope {
  const fft = new FFT(FFT_SIZE);
  const win = hann(FFT_SIZE);
  // Pad the front with silence. Spectral flux needs something to difference
  // against, and a loop that starts on a kick — which is most of them — would
  // otherwise lose its single most important onset.
  const pad = FFT_SIZE;
  const padded = new Float32Array(mono.length + pad);
  padded.set(mono, pad);
  const frames = Math.max(1, Math.floor((padded.length - FFT_SIZE) / HOP));
  const flux = new Float32Array(frames);
  const lowFlux = new Float32Array(frames);
  const re = new Float32Array(FFT_SIZE);
  const im = new Float32Array(FFT_SIZE);
  const bins = FFT_SIZE / 2;
  // Everything below ~200 Hz, which is kick territory.
  const lowBins = Math.max(2, Math.round((200 / (sampleRate / 2)) * bins));
  let prev = new Float32Array(bins);
  let cur = new Float32Array(bins);

  for (let f = 0; f < frames; f++) {
    const start = f * HOP;
    for (let i = 0; i < FFT_SIZE; i++) {
      re[i] = padded[start + i] * win[i];
      im[i] = 0;
    }
    fft.transform(re, im);
    let sum = 0;
    let lowSum = 0;
    for (let b = 0; b < bins; b++) {
      // Log-magnitude compresses dynamics so quiet hats still register.
      const mag = Math.log1p(80 * Math.hypot(re[b], im[b]));
      cur[b] = mag;
      const d = mag - prev[b];
      if (d > 0) {
        sum += d;
        if (b < lowBins) lowSum += d;
      }
    }
    // Frame 0 has nothing to difference against; counting it would produce a
    // spike big enough to flatten every real onset during normalisation.
    flux[f] = f === 0 ? 0 : sum;
    lowFlux[f] = f === 0 ? 0 : lowSum;
    const swap = prev;
    prev = cur;
    cur = swap;
    if (onProgress && (f & 2047) === 0) onProgress(f / frames);
  }
  onProgress?.(1);

  return {
    values: normaliseFlux(flux),
    low: normaliseFlux(lowFlux),
    rate: sampleRate / HOP,
    origin: (FFT_SIZE / 2 - pad) / sampleRate,
  };
}

/** Adaptive-mean subtraction so the envelope sits on zero, then peak-normalise. */
function normaliseFlux(flux: Float32Array): Float32Array {
  const frames = flux.length;
  const win2 = 16;
  const out = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    let acc = 0;
    let cnt = 0;
    for (let k = Math.max(0, f - win2); k < Math.min(frames, f + win2); k++) {
      acc += flux[k];
      cnt++;
    }
    out[f] = Math.max(0, flux[f] - acc / Math.max(1, cnt));
  }
  let peak = 0;
  for (let f = 0; f < frames; f++) peak = Math.max(peak, out[f]);
  if (peak > 0) for (let f = 0; f < frames; f++) out[f] /= peak;
  return out;
}

export interface TempoResult {
  bpm: number;
  confidence: number;
  /** Seconds into the file where the first downbeat sits. */
  offset: number;
}

/** Comb weights: a true beat period lines up with its own multiples too. */
const COMB_WEIGHTS = [1, 0.7, 0.5, 0.35];

/**
 * How plausible a tempo is on its own merits, independent of the audio.
 * Purely periodic material (a hat on every eighth) makes 60, 80, 120 and 240
 * mathematically indistinguishable, so we lean on the well-documented human
 * preference for tempi around 120 to break the tie.
 */
function resonance(bpm: number): number {
  const octaves = Math.log2(bpm / 120);
  return Math.exp(-0.5 * (octaves / 1.1) ** 2);
}

/**
 * How well a tempo divides the file into whole bars.
 *
 * This is the assumption Happy Chopper is allowed to make that a general-purpose
 * tempo tracker isn't: you are loading a loop, so its length is very likely a
 * whole number of bars — usually a power of two. It is what separates a real
 * beat from a 1.5-beat lag, which autocorrelation alone cannot do on steady
 * material. Kept soft (never below 0.4) so odd-length or non-loop material
 * still detects sensibly.
 */
function loopFit(bpm: number, duration: number, beatsPerBar: number): number {
  if (!duration || duration <= 0) return 1;
  const bars = (duration * bpm) / 60 / beatsPerBar;
  const nearest = Math.round(bars);
  if (nearest < 1) return 0.5;
  const err = Math.abs(bars - nearest);
  const fit = 0.4 + 0.6 * Math.exp(-0.5 * (err / 0.05) ** 2);
  const powerOfTwo = (nearest & (nearest - 1)) === 0;
  return fit * (powerOfTwo ? 1.12 : 1);
}

export interface TempoOptions {
  min?: number;
  max?: number;
  beatsPerBar?: number;
  /** True file duration in seconds; enables the whole-bars prior. */
  duration?: number;
}

/**
 * Comb-filtered autocorrelation tempo estimate.
 *
 * The search runs wide (40–320 BPM) so the true pulse is always in range, then
 * the winner is folded into `min`..`max` by octaves.
 */
export function detectTempo(env: OnsetEnvelope, opts: TempoOptions = {}): TempoResult {
  const { min = 70, max = 180, beatsPerBar = 4, duration = 0 } = opts;
  const { values, rate } = env;
  const n = values.length;
  if (n < 32) return { bpm: 120, confidence: 0, offset: 0 };

  let energy = 0;
  for (let i = 0; i < n; i++) energy += values[i] * values[i];
  if (energy <= 0) return { bpm: 120, confidence: 0, offset: 0 };
  const meanSquare = energy / n;

  // Biased normalisation (divide by n, not n-lag). The taper it introduces is
  // wanted: long lags are measured over less overlap and deserve less trust.
  // The lag ceiling matters for long files: without it a five-minute envelope
  // would autocorrelate out to three minutes of lag for no benefit, since the
  // comb never looks past four times the slowest tempo we search for.
  const lagCeiling = Math.ceil((60 / 40) * rate) * COMB_WEIGHTS.length;
  const acfMax = Math.min(n - 2, Math.floor(n * 0.6), lagCeiling);
  const acf = new Float32Array(acfMax + 1);
  for (let lag = 1; lag <= acfMax; lag++) {
    let acc = 0;
    for (let i = 0; i + lag < n; i++) acc += values[i] * values[i + lag];
    acf[lag] = acc / n / meanSquare;
  }

  const minLag = Math.max(2, Math.floor((60 / 320) * rate));
  const maxLag = Math.min(acfMax, Math.ceil((60 / 40) * rate));

  let bestLag = minLag;
  let best = -Infinity;
  let total = 0;
  let count = 0;
  const scored = new Float32Array(maxLag + 1);

  for (let lag = minLag; lag <= maxLag; lag++) {
    let s = 0;
    let wsum = 0;
    for (let k = 0; k < COMB_WEIGHTS.length; k++) {
      const l = lag * (k + 1);
      if (l > acfMax) break;
      s += acf[l] * COMB_WEIGHTS[k];
      wsum += COMB_WEIGHTS[k];
    }
    if (wsum === 0) continue;
    const folded = foldBpm((60 * rate) / lag, min, max);
    s = (s / wsum) * resonance(folded) * loopFit(folded, duration, beatsPerBar);
    scored[lag] = s;
    total += s;
    count++;
    if (s > best) {
      best = s;
      bestLag = lag;
    }
  }

  // Parabolic interpolation around the peak for sub-frame precision. Only
  // valid on a genuine concave peak, and never worth more than half a frame —
  // an unclamped shift on a near-flat neighbourhood lands anywhere.
  let refined = bestLag;
  if (bestLag > minLag && bestLag < maxLag) {
    const a = scored[bestLag - 1];
    const b = scored[bestLag];
    const c = scored[bestLag + 1];
    const denom = a - 2 * b + c;
    if (denom < 0) {
      const shift = (0.5 * (a - c)) / denom;
      refined = bestLag + Math.max(-0.5, Math.min(0.5, shift));
    }
  }

  const bpm = foldBpm((60 * rate) / refined, min, max);
  const mean = count ? total / count : 0;
  const confidence = mean > 0 ? Math.max(0, Math.min(1, (best / mean - 1) / 2)) : 0;

  return { bpm, confidence, offset: beatPhase(env, bpm) };
}

function foldBpm(bpm: number, min: number, max: number): number {
  let b = bpm;
  while (b < min) b *= 2;
  while (b > max) b /= 2;
  return b;
}

/**
 * Slides a pulse train across the envelope to find where beat 1 lands.
 *
 * Loops are nearly always trimmed to their downbeat, so equally-good phases
 * are broken in favour of the earliest one — otherwise a hat on the offbeat
 * can drag the whole grid half a beat late.
 */
export function beatPhase(env: OnsetEnvelope, bpm: number): number {
  const { values, rate, origin } = env;
  const period = (60 / bpm) * rate;
  const pulses = Math.max(1, Math.floor(values.length / period) - 1);
  const steps = Math.max(16, Math.round(period));
  let bestPhase = 0;
  let best = -Infinity;
  for (let s = 0; s < steps; s++) {
    const phase = (s / steps) * period;
    let acc = 0;
    for (let p = 0; p < pulses; p++) {
      const idx = Math.round(phase + p * period);
      if (idx < values.length) acc += values[idx];
    }
    acc *= 1 - 0.35 * (s / steps);
    if (acc > best) {
      best = acc;
      bestPhase = phase;
    }
  }
  let seconds = bestPhase / rate + origin;
  // A downbeat landing in the last sliver of a beat is almost always a
  // marginally early read of the beat at zero, not a genuinely late start.
  const periodSec = 60 / bpm;
  if (seconds > periodSec * 0.85) seconds -= periodSec;
  return Math.max(0, seconds);
}

/** Peak-picked transient positions in seconds. */
export function detectTransients(env: OnsetEnvelope, sensitivity = 0.18): number[] {
  const { values, rate, origin } = env;
  const out: number[] = [];
  const minGap = Math.round(0.03 * rate);
  let last = -minGap;
  for (let i = 1; i < values.length - 1; i++) {
    const v = values[i];
    if (v < sensitivity) continue;
    if (v <= values[i - 1] || v < values[i + 1]) continue;
    if (i - last < minGap) continue;
    last = i;
    out.push(i / rate + origin);
  }
  return out;
}

/** A time window of an envelope, with `origin` kept consistent. */
export function sliceEnvelope(env: OnsetEnvelope, fromSec: number, toSec: number): OnsetEnvelope {
  const a = Math.max(0, Math.floor((fromSec - env.origin) * env.rate));
  const b = Math.min(env.values.length, Math.ceil((toSec - env.origin) * env.rate));
  if (b <= a) return env;
  return {
    values: env.values.subarray(a, b),
    low: env.low.subarray(a, b),
    rate: env.rate,
    origin: env.origin + a / env.rate,
  };
}

export interface GridFit {
  bpm: number;
  /** Seconds to the first downbeat. */
  offset: number;
  /** Mean onset strength landing on the grid, 0..1-ish. */
  strength: number;
}

/** Reads the envelope at a fractional frame position. */
function sampleAt(values: Float32Array, pos: number): number {
  if (pos < 0 || pos >= values.length - 1) return 0;
  const i = Math.floor(pos);
  const f = pos - i;
  return values[i] * (1 - f) + values[i + 1] * f;
}

/** Floor inside the geometric mean, so one silent beat isn't fatal. */
const GRID_FLOOR = 0.03;

/** Beats sampled per evaluation. Plenty statistically, and bounds the cost. */
const GRID_POINTS = 400;

/**
 * How well a beat grid of this period and phase explains the onsets.
 *
 * The *geometric* mean is the important part. An arithmetic mean can't tell
 * 128 BPM from 102.4 on sixteenth-note material, because a grid four-fifths
 * too slow still lands on the sixteenth grid forever — it just cycles through
 * different positions. Averaging in the log domain punishes a grid that
 * sometimes lands on nothing, which is exactly what a wrong ratio does, while
 * a correct grid keeps hitting something every time.
 */
function gridScore(
  values: Float32Array,
  period: number,
  phase: number,
  geometric: boolean,
  maxPoints = GRID_POINTS,
): number {
  if (period <= 0) return 0;
  const available = Math.floor((values.length - 1 - phase) / period);
  if (available <= 0) return 0;
  const stride = Math.max(1, Math.ceil(available / maxPoints));
  const step = period * stride;
  let acc = 0;
  let count = 0;
  for (let p = phase; p < values.length - 1; p += step) {
    const v = sampleAt(values, p);
    acc += geometric ? Math.log(v + GRID_FLOOR) : v;
    count++;
  }
  if (!count) return 0;
  return geometric ? Math.exp(acc / count) - GRID_FLOOR : acc / count;
}

/** Seconds covered by an envelope. */
function envelopeDuration(env: OnsetEnvelope): number {
  return env.origin + env.values.length / env.rate;
}

/** A window of `seconds` taken from the middle of the file. */
function middleWindow(env: OnsetEnvelope, seconds: number): OnsetEnvelope {
  const total = envelopeDuration(env);
  if (seconds >= total) return env;
  const mid = total / 2;
  return sliceEnvelope(env, mid - seconds / 2, mid + seconds / 2);
}

interface LocalFit {
  bpm: number;
  /** Frames. */
  phase: number;
  score: number;
}

/**
 * Joint (tempo, phase) search in a narrow window around `bpm`.
 *
 * Phase is oversampled four times per frame. A one-frame phase grid is far too
 * coarse — onsets in the envelope are only a few frames wide, so a correct
 * tempo evaluated at the wrong phase scores like a wrong tempo, which is how a
 * 128 BPM track ends up reported as 102.4.
 */
function localRefine(
  env: OnsetEnvelope,
  bpm: number,
  span: number,
  steps: number,
  geometric: boolean,
): LocalFit {
  const { values, rate } = env;
  const lo = bpm * (1 - span);
  const hi = bpm * (1 + span);
  let best = -Infinity;
  let bestBpm = bpm;
  let bestPhase = 0;
  for (let s = 0; s < steps; s++) {
    const candidate = steps === 1 ? bpm : lo + ((hi - lo) * s) / (steps - 1);
    const period = (60 / candidate) * rate;
    const phaseSteps = Math.max(24, Math.min(512, Math.round(period * 4)));
    for (let q = 0; q < phaseSteps; q++) {
      const ph = (q / phaseSteps) * period;
      const score = gridScore(values, period, ph, geometric);
      if (score > best) {
        best = score;
        bestBpm = candidate;
        bestPhase = ph;
      }
    }
  }
  return { bpm: bestBpm, phase: bestPhase, score: best };
}

/**
 * Metrical relatives to try when the coarse estimate might be on the wrong
 * subdivision. Covers the usual halves and doubles plus the 3-against-2 and
 * 4-against-5 traps that dense sixteenth-note material sets.
 */
const TEMPO_RATIOS = [1, 1 / 2, 2, 2 / 3, 3 / 2, 3 / 4, 4 / 3, 4 / 5, 5 / 4, 1 / 3, 3, 1 / 4, 4];

/**
 * Decides the tempo of a whole track.
 *
 * Autocorrelation reliably finds *a* pulse but routinely picks the wrong
 * multiple of it — on the test material it settled on 102.4 for a 128 BPM
 * track, exactly four fifths. Rather than trust it, every musically plausible
 * relative of the coarse estimate is scored against the full file, where a
 * wrong ratio has nowhere to hide.
 */
export function lockTempo(
  env: OnsetEnvelope,
  coarseBpm: number,
  beatsPerBar = 4,
  min = 60,
  max = 200,
): GridFit {
  const seen = new Set<number>();
  let winner = coarseBpm;
  let winnerScore = -Infinity;

  // Judge candidates on a short window. The geometric score is decisive but
  // very sensitive to tempo error, and over a whole file a candidate that is
  // off by a fraction of a BPM drifts off the onsets and scores like a wrong
  // answer. Thirty seconds is long enough to be sure and short enough to be
  // forgiving.
  const scope = middleWindow(env, 30);

  for (const ratio of TEMPO_RATIOS) {
    let bpm = coarseBpm * ratio;
    while (bpm < min) bpm *= 2;
    while (bpm > max) bpm /= 2;
    const key = Math.round(bpm * 4);
    if (seen.has(key)) continue;
    seen.add(key);

    const fit = localRefine(scope, bpm, 0.03, 41, true);
    const score = fit.score * resonance(fit.bpm);
    if (score > winnerScore) {
      winnerScore = score;
      winner = fit.bpm;
    }
  }

  return refineTempo(env, winner, beatsPerBar);
}

/**
 * Locks a coarse tempo estimate to the whole file.
 *
 * `detectTempo` picks the right octave but its precision is limited by the
 * frame rate, and over four minutes a 0.5% error walks the grid off by seconds.
 * This does a two-stage joint search over tempo and phase against every beat in
 * the file, interpolating between frames — the more beats there are, the more
 * precisely the tempo is pinned, so long files come out *better* than loops.
 *
 * Finally it decides which of the `beatsPerBar` beats is the downbeat by
 * looking for the accented one, so extracted loops start on bar 1.
 */
export function refineTempo(
  env: OnsetEnvelope,
  bpmGuess: number,
  beatsPerBar = 4,
): GridFit {
  const { rate, origin } = env;
  const total = envelopeDuration(env);

  /**
   * Hierarchical: start on twenty seconds with a wide span, then widen the
   * window and narrow the span together. The score surface gets sharper the
   * more beats it covers — a peak only 0.01% wide over four minutes — so a
   * single flat sweep of the whole file would step straight over it.
   */
  const STEPS = 41;
  let fit: LocalFit = { bpm: bpmGuess, phase: 0, score: 0 };
  let windowSec = 20;
  let span = 0.03;
  for (let stage = 0; stage < 8; stage++) {
    const scope = windowSec >= total ? env : middleWindow(env, windowSec);
    fit = localRefine(scope, fit.bpm, span, STEPS, false);
    if (windowSec >= total) break;
    // Next span is twice this stage's step size, which is what its accuracy is
    // worth — narrow enough to gain precision, wide enough to still contain
    // the answer.
    span = (2 * span) / (STEPS - 1);
    windowSec = Math.min(total, windowSec * 3);
  }

  // The final fit came from a centred window; re-solve the phase against the
  // whole file so bar 1 is right at the start, not just in the middle.
  const finalPeriod = (60 / fit.bpm) * rate;
  let phaseBest = -Infinity;
  let phase = fit.phase;
  const phaseSteps = Math.max(24, Math.min(512, Math.round(finalPeriod * 4)));
  for (let q = 0; q < phaseSteps; q++) {
    const ph = (q / phaseSteps) * finalPeriod;
    const s = gridScore(env.values, finalPeriod, ph, false);
    if (s > phaseBest) {
      phaseBest = s;
      phase = ph;
    }
  }
  const bpm = fit.bpm;
  const best = phaseBest;

  // Which beat of the bar carries the kick? That one is the downbeat.
  const period = (60 / bpm) * rate;
  let bestBeat = 0;
  let bestBeatScore = -Infinity;
  for (let b = 0; b < beatsPerBar; b++) {
    const s = gridScore(env.low, period * beatsPerBar, phase + b * period, false);
    if (s > bestBeatScore) {
      bestBeatScore = s;
      bestBeat = b;
    }
  }

  let offset = (phase + bestBeat * period) / rate + origin;
  // Wind back to the earliest downbeat that still sits inside the file.
  const barSec = (60 / bpm) * beatsPerBar;
  while (offset - barSec >= -0.01) offset -= barSec;

  return { bpm, offset: Math.max(0, offset), strength: best };
}

/** Downsampled min/max pairs for waveform drawing, straight from samples. */
export function peaksFromMono(mono: Float32Array, buckets = 4096): Float32Array {
  const out = new Float32Array(buckets * 2);
  const per = mono.length / buckets;
  for (let b = 0; b < buckets; b++) {
    const s = Math.floor(b * per);
    const e = Math.min(mono.length, Math.floor((b + 1) * per));
    let lo = 0;
    let hi = 0;
    for (let i = s; i < e; i++) {
      const v = mono[i];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    out[b * 2] = lo;
    out[b * 2 + 1] = hi;
  }
  return out;
}

/** Downsampled min/max pairs for waveform drawing. */
export function computePeaks(buffer: AudioBuffer, buckets = 4096): Float32Array {
  const mono = toMono(buffer);
  const out = new Float32Array(buckets * 2);
  const per = mono.length / buckets;
  for (let b = 0; b < buckets; b++) {
    const s = Math.floor(b * per);
    const e = Math.min(mono.length, Math.floor((b + 1) * per));
    let lo = 0;
    let hi = 0;
    for (let i = s; i < e; i++) {
      const v = mono[i];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    out[b * 2] = lo;
    out[b * 2 + 1] = hi;
  }
  return out;
}
